type UseIntercomParams = {
  intercomIsActive?: boolean;
  intercomKey?: string;
  intercomAppId?: string | null;
  userEmail?: string;
  intercomUserHash?: string | null;
};

type IntercomProps = {
  initializeIntercom: () => void;
  removeIntercomTags: () => void;
};

const INTERCOM_SCRIPT_BASE = "https://widget.intercom.io/widget/";

const getScriptSnippet = (
  appID: string | null,
  userEmail: string,
  userHash: string | null,
): string => `
    window.intercomSettings = {
      api_base: "https://api-iam.intercom.io",
      app_id: "${appID}",
      email: "${userEmail}",
      user_hash: "${userHash}"
    };
    
    (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',intercomSettings);}else{var
  d=document;var i=function(){i.c(arguments)};i.q=[];i.c=function(args){i.q.push(args)};w.Intercom=i;function
  l(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${appID}';var
  x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);}if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})()
`;

const useIntercom = ({
  intercomIsActive = false,
  intercomKey = "",
  intercomAppId = "",
  userEmail = "",
  intercomUserHash = "",
}: UseIntercomParams): IntercomProps => {
  const removeExistingTags = (): void => {
    document.querySelectorAll("script").forEach((element) => {
      const { src, innerHTML } = element;

      if (
        element instanceof HTMLScriptElement &&
        (src.includes(INTERCOM_SCRIPT_BASE) || innerHTML.includes(INTERCOM_SCRIPT_BASE)) &&
        (element.getAttribute("data-intercom-settings") === "true" ||
          element.getAttribute("data-intercom-widget") === "true")
      ) {
        element.remove();
      }
    });
  };

  const createIntercomSettingsScript = (): HTMLScriptElement | Element => {
    const existingSettingsScript = document.querySelector("script[data-intercom-settings]");
    if (!existingSettingsScript) {
      const script = document.createElement("script");
      script.setAttribute("data-intercom-settings", "true");
      script.innerHTML = getScriptSnippet(intercomAppId, userEmail, intercomUserHash);
      return script;
    }
    return existingSettingsScript;
  };

  const createIntercomWidgetScript = (): HTMLScriptElement | Element => {
    const existingWidgetScript = document.querySelector("script[data-intercom-widget]");
    if (!existingWidgetScript) {
      const script = document.createElement("script");
      script.setAttribute("data-intercom-widget", "true");
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${intercomAppId}`;
      return script;
    }
    return existingWidgetScript;
  };

  const initializeIntercom = (): void => {
    if (!intercomKey || !intercomIsActive || !intercomAppId) {
      removeExistingTags();
      return;
    }
    removeExistingTags();
    document.head.insertAdjacentElement("afterbegin", createIntercomWidgetScript());
    document.head.insertAdjacentElement("afterbegin", createIntercomSettingsScript());
  };

  return { initializeIntercom, removeIntercomTags: removeExistingTags };
};

export default useIntercom;
